import React, { useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ResultBadge } from "./ResultBadge";
import { Gavel, Filter } from "lucide-react";

const FILTERS = ["ALL", "FAIL", "NEEDS_REVIEW", "PASS", "NOT_APPLICABLE"];

export function RuleAuditPanel({ cid, onChange }) {
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [drill, setDrill] = useState(null);

  const load = async () => { const r = await api.get(`/campaigns/${cid}/audit`); setData(r.data); };
  React.useEffect(() => { load(); }, [cid]); // eslint-disable-line

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/campaigns/${cid}/audit/run`);
      setData(r.data);
      const s = r.data.run.status;
      toast[s === "blocked" ? "error" : s === "review" ? "warning" : "success"](`Rule audit ${s} · ${r.data.run.counts.FAIL} fail / ${r.data.run.counts.NEEDS_REVIEW} review`);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Audit failed"); } finally { setBusy(false); }
  };
  const openDrill = async (id) => { const r = await api.get(`/audit-results/${id}`); setDrill(r.data); };

  const results = useMemo(() => (data?.results || []).filter((r) => filter === "ALL" || r.result === filter), [data, filter]);
  const run_ = data?.run;
  return (
    <div className="space-y-5" data-testid="rule-audit-panel">
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2">
            <span className="font-heading text-lg font-bold text-slate-900">Deterministic Rule Audit · FR-03</span>
            {run_ && <ResultBadge value={run_.status} data-testid="audit-status" />}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {run_ ? `Rule set ${run_.rule_version} · ${run_.rules_evaluated} rules · ${run_.results_count} evaluations · ${new Date(run_.executed_at).toLocaleString()} by ${run_.executed_by}`
              : "No audit executed yet. Rules are deterministic — financial and compliance checks never depend on generative AI."}
          </p>
        </div>
        {run_ && (
          <div className="flex gap-3 text-center" data-testid="audit-counts">
            {[["PASS", run_.counts.PASS, "text-emerald-600"], ["FAIL", run_.counts.FAIL, "text-rose-600"], ["REVIEW", run_.counts.NEEDS_REVIEW, "text-amber-600"], ["N/A", run_.counts.NOT_APPLICABLE, "text-slate-400"]].map(([l, v, cls]) => (
              <div key={l} className="min-w-[56px]"><div className={`font-mono text-2xl font-bold ${cls}`}>{v}</div><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div></div>
            ))}
          </div>
        )}
        <Button onClick={run} disabled={busy} className="bg-[#0052FF] hover:bg-[#0043d1]" data-testid="audit-run-btn"><Gavel size={16} className="mr-1" /> {run_ ? "Re-run audit" : "Run audit"}</Button>
      </div>

      {run_ && (
        <>
          <div className="flex items-center gap-2 text-xs">
            <Filter size={14} className="text-slate-400" />
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`audit-filter-${f}`} className={`rounded-full px-3 py-1 font-semibold transition-colors ${filter === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{f.replace("_", " ")}</button>
            ))}
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-2">Rule</th><th className="px-4 py-2">Scope</th><th className="px-4 py-2">Result</th><th className="px-4 py-2">Severity</th><th className="px-4 py-2">Reason</th><th className="px-4 py-2"></th></tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`audit-result-${r.rule_code}`}>
                    <td className="px-4 py-2.5"><div className="font-semibold text-slate-800">{r.rule_name}</div><div className="font-mono text-[11px] text-slate-400">{r.rule_code} · v{r.rule_version}</div></td>
                    <td className="px-4 py-2.5 text-slate-600"><span className="text-[10px] uppercase tracking-wider text-slate-400">{r.scope_type}</span><br />{r.scope_label}</td>
                    <td className="px-4 py-2.5"><ResultBadge value={r.result} /></td>
                    <td className="px-4 py-2.5"><ResultBadge value={r.severity} /></td>
                    <td className="px-4 py-2.5 text-slate-700">{r.reason}</td>
                    <td className="px-4 py-2.5 text-right"><button onClick={() => openDrill(r.id)} className="text-[#0052FF] text-xs font-semibold hover:underline" data-testid={`audit-drill-${r.id}`}>Evidence →</button></td>
                  </tr>
                ))}
                {results.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400">No results for this filter</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Sheet open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto" data-testid="audit-evidence-sheet">
          {drill && (
            <>
              <SheetHeader><SheetTitle>{drill.rule_code} · {drill.rule_name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="flex gap-2"><ResultBadge value={drill.result} /><ResultBadge value={drill.severity} /></div>
                <p className="text-slate-700">{drill.reason}</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-slate-500">
                  <div>Rule version <span className="font-mono text-slate-800">{drill.rule_version}</span></div>
                  <div>Executed <span className="font-mono text-slate-800">{new Date(drill.executed_at).toLocaleString()}</span></div>
                  <div>Scope <span className="text-slate-800">{drill.scope_type} · {drill.scope_label}</span></div>
                </div>
                <Block title="Evidence" obj={drill.evidence} />
                <Block title={`Source record (${drill.scope_type})`} obj={drill.source_record} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Block({ title, obj }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{title}</div>
      <pre className="rounded-lg bg-slate-900 text-slate-100 p-3 text-[11px] leading-relaxed overflow-x-auto font-mono max-h-72">{JSON.stringify(obj, null, 2)}</pre>
    </div>
  );
}
